import React, { useState, useEffect } from "react";
import client from "../../lib/api";
import { AGENT_STAGES, telHref, waHref, mailHref } from "../../mock";
import { useCities } from "../../hooks/useCities";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { GripVertical, Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../../i18n/LangContext";

export default function AgentPipeline() {
  const [agents, setAgents] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [cityFilter, setCityFilter] = useState("all");
  const { cities: cityList } = useCities();

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/agents");
        setAgents(res.data);
      } catch (e) { toast.error(tr("Couldn't load agents")); }
    })();
  }, []);

  const onDrop = async (stageId) => {
    if (!dragId) return;
    const moved = agents.find((a) => a.id === dragId);
    const current = dragId;
    setDragId(null);
    if (!moved || (moved.stage || "contact") === stageId) return;
    setAgents((prev) => prev.map((a) => (a.id === current ? { ...a, stage: stageId } : a)));
    const stage = AGENT_STAGES.find((s) => s.id === stageId);
    try {
      await client.put(`/agents/${current}`, { stage: stageId });
      toast.success(`${moved.name} → ${stage.label}`);
    } catch (e) {
      setAgents((prev) => prev.map((a) => (a.id === current ? { ...a, stage: moved.stage } : a)));
      toast.error(tr("Couldn't move agent"));
    }
  };

  const ContactIcon = ({ href, icon: Icon, label, cls }) => {
    if (!href) return null;
    return (
      <a href={href} target="_blank" rel="noreferrer" title={tr(label)} onClick={(e) => e.stopPropagation()}
        data-testid={`agent-${label.toLowerCase()}`}
        className={`grid h-7 w-7 place-items-center rounded-lg border transition-colors ${cls}`}>
        <Icon className="h-3.5 w-3.5" />
      </a>
    );
  };

  const cities = cityList || [];
  const shownAgents = cityFilter === "all"
    ? agents
    : agents.filter((a) => a.city === cityFilter || !a.city);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-white">{tr("Agent CRM")}</h2>
          <p className="text-xs text-muted-foreground">{tr("Drag agents through your sourcing pipeline")}{cityFilter !== "all" ? ` · ${cityFilter} (${shownAgents.length})` : ""}</p>
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="h-10 w-[190px] border-white/12 bg-white/[0.03] text-white" data-testid="agent-pipeline-city-filter">
            <MapPin className="h-4 w-4 text-muted-foreground" /><SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-strong border-white/10">
            <SelectItem value="all">{tr("All cities")}</SelectItem>
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar" data-testid="agent-pipeline-board">
        {AGENT_STAGES.map((stage) => {
          const items = shownAgents.filter((a) => (a.stage || "contact") === stage.id);
          return (
            <div key={stage.id} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage.id)}
              className="flex w-[280px] shrink-0 flex-col rounded-2xl border border-white/[0.06] bg-card/30"
              data-testid={`agent-col-${stage.id}`}>
              <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: stage.color }} />
                  <span className="text-sm font-semibold text-white">{tr(stage.label)}</span>
                </div>
                <span className="rounded-full bg-white/5 px-1.5 text-xs text-muted-foreground">{items.length}</span>
              </div>
              <div className="flex-1 space-y-3 p-3 min-h-[120px]">
                {items.map((a) => (
                  <div key={a.id} draggable onDragStart={() => setDragId(a.id)} onDragEnd={() => setDragId(null)}
                    className={`group cursor-grab rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 transition-all hover:border-white/15 active:cursor-grabbing ${dragId === a.id ? "opacity-40" : ""}`}
                    data-testid="agent-pipeline-card">
                    <div className="flex items-start gap-3">
                      <img src={a.avatar} alt="" className="h-10 w-10 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-white">{a.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{a.agency}</p>
                        {a.city && <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-brand-green"><MapPin className="h-3 w-3" />{a.city}</p>}
                      </div>
                      <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <ContactIcon href={telHref(a.phone)} icon={Phone} label="Call" cls="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.08]" />
                      <ContactIcon href={waHref(a.whatsapp || a.phone)} icon={MessageCircle} label="WhatsApp" cls="border-brand-green/30 bg-brand-green/10 text-brand-green hover:bg-brand-green/20" />
                      <ContactIcon href={mailHref(a.email)} icon={Mail} label="Email" cls="border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.08]" />
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="grid h-24 place-items-center rounded-xl border border-dashed border-white/8 text-xs text-muted-foreground">{tr("Drop agents here")}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
